import React, { useReducer, useCallback, useMemo, useEffect } from "react";
import { PermitType } from "common/types/permits";
import { PermitApplicationContext } from "./PermitApplicationContext";
import { IPermitApplicationContext, PermitApplicationStep, PermitApplicationState } from "./types";
import {
  permitApplicationReducer,
  INITIAL_STATE,
  SET_AUTHORITY,
  SET_PERMIT_TYPE,
  SET_SELECTED_VEHICLES,
  UPDATE_DOCUMENT,
  CLEAR_DOCUMENT,
  ACCEPT_TERMS,
  CLEAR_TERMS,
  RESET_APPLICATION,
} from "./reducer";

/**
 * Check if a specific step is complete based on current state
 */
function checkStepComplete(state: PermitApplicationState, step: PermitApplicationStep): boolean {
  switch (step) {
    case PermitApplicationStep.SelectAuthority:
      return state.authority.authorityId !== null;

    case PermitApplicationStep.SelectPermitType:
      return state.permitTypeSelection.permitTypeId !== null;

    case PermitApplicationStep.ProvideInformation:
      return (
        state.permitTypeSelection.permitType?.vehicleScope === "per_account" ||
        state.vehicleSelection.selectedVehicleIds.length > 0 ||
        state.vehicleSelection.applyToAllVehicles
      );

    case PermitApplicationStep.UploadDocuments:
      // Complete if no documents required OR all documents have been uploaded
      return (
        state.permitTypeSelection.permitTypeId !== null &&
        (state.documents.length === 0 || state.documents.every((doc) => doc.documentId !== null))
      );

    case PermitApplicationStep.ReviewConfirm:
      return state.acceptedTermsAt !== null;

    default:
      return false;
  }
}

/**
 * Check if user can proceed to a specific step (all previous steps must be complete)
 */
function checkCanProceedToStep(state: PermitApplicationState, step: PermitApplicationStep): boolean {
  // Can always access step 1
  if (step === PermitApplicationStep.SelectAuthority) {
    return true;
  }

  // Check all previous steps are complete
  for (let i = 1; i < step; i++) {
    if (!checkStepComplete(state, i as PermitApplicationStep)) {
      return false;
    }
  }

  return true;
}

export interface PermitApplicationProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that manages permit application form state
 * State persists during the session but is not saved to backend
 */
export const PermitApplicationProvider: React.FC<PermitApplicationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(permitApplicationReducer, INITIAL_STATE);

  // Reset application state on unmount for clean re-entry
  useEffect(() => {
    return () => {
      dispatch({ type: RESET_APPLICATION });
    };
  }, []);

  const setAuthority = useCallback((authorityId: string | null, authorityName: string | null) => {
    dispatch({
      type: SET_AUTHORITY,
      payload: { authorityId, authorityName },
    });
  }, []);

  const setPermitType = useCallback((permitTypeId: string, permitType: PermitType) => {
    dispatch({
      type: SET_PERMIT_TYPE,
      payload: { permitTypeId, permitType },
    });
  }, []);

  const setSelectedVehicles = useCallback(
    (vehicleIds: string[], applyToAllVehicles: boolean, autoAssignNewVehicles: boolean) => {
      dispatch({
        type: SET_SELECTED_VEHICLES,
        payload: { vehicleIds, applyToAllVehicles, autoAssignNewVehicles },
      });
    },
    [],
  );

  const updateDocument = useCallback((documentTypeId: string, documentId: string) => {
    dispatch({
      type: UPDATE_DOCUMENT,
      payload: { documentTypeId, documentId },
    });
  }, []);

  const clearDocument = useCallback((documentTypeId: string) => {
    dispatch({
      type: CLEAR_DOCUMENT,
      payload: { documentTypeId },
    });
  }, []);

  const acceptTerms = useCallback(() => {
    dispatch({ type: ACCEPT_TERMS });
  }, []);

  const clearTerms = useCallback(() => {
    dispatch({ type: CLEAR_TERMS });
  }, []);

  const resetApplication = useCallback(() => {
    dispatch({ type: RESET_APPLICATION });
  }, []);

  const isStepComplete = useCallback((step: PermitApplicationStep) => checkStepComplete(state, step), [state]);

  const canProceedToStep = useCallback((step: PermitApplicationStep) => checkCanProceedToStep(state, step), [state]);

  const contextValue: IPermitApplicationContext = useMemo(
    () => ({
      state,
      setAuthority,
      setPermitType,
      setSelectedVehicles,
      updateDocument,
      clearDocument,
      acceptTerms,
      clearTerms,
      resetApplication,
      isStepComplete,
      canProceedToStep,
    }),
    [
      state,
      setAuthority,
      setPermitType,
      setSelectedVehicles,
      updateDocument,
      clearDocument,
      acceptTerms,
      clearTerms,
      resetApplication,
      isStepComplete,
      canProceedToStep,
    ],
  );

  return <PermitApplicationContext.Provider value={contextValue}>{children}</PermitApplicationContext.Provider>;
};

export default PermitApplicationProvider;
